import { useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MarkInactiveModal,
  MiModalConfirm,
  PrioritySchedulerBoard,
  RemoveInactiveModal,
} from "@miplatform/components";
import { Colors } from "@miplatform/types";
import { mdiCancel } from "@mdi/js";
import { Box } from "@mui/material";
import { AssignFromFollowUpModal } from "./modals/AssignFromFollowUpModal/AssignFromFollowUpModal";
import { FOLLOW_UP_TRAY_FILTERS } from "../../Config/Calendars/ConfigCalendarSetList.constants";
import { CalendarToolBar } from "../CalendarToolBar/CalendarToolBar";
import { NHCWorkOrderCard } from "./NHCWorkOrderCard";
import { useSchedulerViewViewModel } from "./useSchedulerViewViewModel";

export function SchedulerView() {
  const { theme, classes } = useStyles();
  const {
    zoom,
    setZoom,
    showFilters,
    toggleFilters,
    filters,
    appliedFilters,
    statusOptions,
    hasPendingFilters,
    handleApplyFilters,
    handleResetFilters,
    handleFilterChange,
    workTemplateBases,
    showToolbarBackBtn,
    handleBack,
    toolbarTitle,
    columns,
    date,
    highlightedAttempts,
    tenantHolidays,
    calendarSetConfig,
    headerActions,
    unhighlightAttempts,
    handleHeaderActionClick,
    setSelectedAttempt,
    activeTrays,
    isAutoAssignLoading,
    isAutoAssignActive,
    isAssigneeMode,
    handleFollowUpAutoAssign,
    getTrayDataForCalendar,
    handleSelectUser,
    clearScheduleModalUser,
    closeClearScheduleModal,
    handleClearScheduleConfirm,
    isCalendarLoading,
    followUpModalUser,
    closeFollowUpModal,
    assignedAttemptsForFollowUpUser,
    followUpAttempts,
    removeInactiveModalUser,
    closeRemoveInactiveModal,
    markInactiveModalUser,
    closeMarkInactiveModal,
    loadUsers,
    selectedDate,
  } = useSchedulerViewViewModel();

  useEffect(() => {
    getTrayDataForCalendar();
  }, [calendarSetConfig?.id]);

  useEffect(() => {
    handleSelectUser(null);
  }, []);

  return (
    <Box className={classes.container}>
      <Box className={classes.topRow}>
        <CalendarToolBar
          calendarSetId={calendarSetConfig?.id}
          zoom={zoom}
          setZoom={setZoom}
          showCalendarFilters={showFilters}
          toggleCalendarFilters={toggleFilters}
          filters={filters}
          appliedFilters={appliedFilters}
          statusOptions={statusOptions}
          hasPendingFilters={hasPendingFilters}
          onApplyFilters={handleApplyFilters}
          onResetFilters={handleResetFilters}
          handleFilterChange={handleFilterChange}
          workTemplateBases={workTemplateBases}
          onBack={showToolbarBackBtn ? handleBack : undefined}
          title={toolbarTitle}
        />
      </Box>
      <Box className={classes.boardWrapper}>
        <PrioritySchedulerBoard
          columns={columns}
          date={date}
          zoom={zoom}
          columnWidth={180}
          hourHeight={60}
          highlightedEventsMap={highlightedAttempts}
          tenantHolidays={tenantHolidays}
          calendarSet={calendarSetConfig}
          headerActions={headerActions}
          onEventUnhighlight={unhighlightAttempts}
          onHeaderActionClick={handleHeaderActionClick}
          onCardClick={setSelectedAttempt}
          EventComponent={NHCWorkOrderCard}
          trays={activeTrays}
          followUpTrayFilter={FOLLOW_UP_TRAY_FILTERS}
          workBaseTemplates={workTemplateBases}
          isAutoAssignLoading={isAutoAssignLoading}
          isAutoAssignActive={isAutoAssignActive}
          isAssigneeMode={isAssigneeMode}
          onFollowUpAutoAssign={handleFollowUpAutoAssign}
        />
      </Box>
      {clearScheduleModalUser && (
        <MiModalConfirm
          title={`Clear ${clearScheduleModalUser?.displayName}'s Schedule?`}
          titleIcon={{
            path: mdiCancel,
            color: theme.palette.primary.main,
          }}
          disableClick={isCalendarLoading}
          textPositive="Clear Schedule"
          open={!!clearScheduleModalUser}
          handleNegative={closeClearScheduleModal}
          handlePositive={handleClearScheduleConfirm}
          description={`You are about clear ${clearScheduleModalUser?.displayName}'s schedule. Would you like to proceed?`}
          buttonPositiveColor={Colors.Primary}
        />
      )}
      {followUpModalUser && (
        <AssignFromFollowUpModal
          open={!!followUpModalUser}
          onClose={closeFollowUpModal}
          user={followUpModalUser}
          assignedAttempts={assignedAttemptsForFollowUpUser}
          followUpAttempts={followUpAttempts}
          calendarSet={calendarSetConfig}
        />
      )}
      {removeInactiveModalUser && (
        <RemoveInactiveModal
          {...({
            open: !!removeInactiveModalUser,
            onClose: closeRemoveInactiveModal,
            user: removeInactiveModalUser,
            onRefresh: loadUsers,
          } as React.ComponentProps<typeof RemoveInactiveModal>)}
        />
      )}
      {markInactiveModalUser && (
        <MarkInactiveModal
          {...({
            open: !!markInactiveModalUser,
            onClose: closeMarkInactiveModal,
            user: markInactiveModalUser,
            onRefresh: loadUsers,
            calendarDate: selectedDate,
          } as React.ComponentProps<typeof MarkInactiveModal>)}
        />
      )}
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  container: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  topRow: {
    backgroundColor: theme.palette.background.default,
  },
  boardWrapper: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
}));
